import React, { useState } from 'react';
import { homeSettingState } from '@atom/setting/homeSettingState';
import CharacterPanel from '@template/CharacterPanel/CharacterPanel';
import styled from 'styled-components';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import SubMenuList from '../common/SubMenuList';
import { gameUiVisibleState } from '@atom/ui/gameUiState';
import CharacterFuncSec from '@template/CharacterPanel/CharacterFuncSec';
import NotificationBtn from '@template/Notifications/NotificationBtn';
import MusicButton from '@template/MusicPlayer/MusicBtn';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import useValidLogin from '@hooks/useValidLogin';
import UserSec from '../common/UserSec';
import Button from '@components/dataEntry/button/Button';
import { registerOpenState } from '@atom/modal/modal';
import GameUiToggleBtn from '../GameUINav/GameUiToggleBtn';
import ActionButtons from '@template/CharacterPanel/ActionButtons';
import { CHAR_PANEL_TYPE } from '@typings/menu/charPanelType';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import SideMenuPanel from '../common/SideMenuPanel';
import { masterSet } from '@masterSetting/masterSet/masterSetting';
import ChatBtn from '@pages/Chat/ChatBtn';
import { POSTING_NOTICE_TYPE } from '@typings/mainHome/postingNotice';
import MiniQuest from '@pages/Quest/MiniQuest/MiniQuest';
import CumuNotice from '@template/CumuNotice/CumuNotice';
import Icon from '@components/dataDisplay/Icons/Icon';
import SideModalWrapper from '@components/wrapper/SideModalWrapper';

const Wrapper = styled.nav<{
  fixed?: boolean;
}>`
  position: ${({ fixed }) => (fixed ? 'fixed' : 'absolute')};
  top: 0px;
  left: 0px;
  width: 100%;
  z-index: 3;
`;
const FrontBtnWrap = styled.div`
  position: fixed;
  top: 6px;
  right: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;

  @media (max-width: 480px) {
    padding: 6px 12px;
    z-index: 3;
    gap: 0;
  }
`;

const UserWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 20px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--lineColor);
`;


const MiniQuestWrapper = styled.div<{ isVisible: boolean }>`
  width: 100%;
  position: fixed;
  top: 300px;
  width: 320px;
  left: 24px;
  z-index: 1;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  /* MiniQuest 슬라이드 애니메이션 (왼쪽으로) */
  transform: ${(props) =>
    props.isVisible
      ? 'translateX(0) translateY(-50%)'
      : 'translateX(calc(-100% - 50px)) translateY(-50%)'};
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.isVisible ? 1 : 0)};

  /* 모바일에서는 별도 사이드 패널로 표시하므로 숨김 */
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileTriggerBtn = styled.button`
  position: fixed;
  left: 12px;
  top: 100px;
  z-index: 3;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  border: 1px solid var(--lineColor);
  background: var(--menuBackgroundColor);
  color: var(--menuTextColor);
  display: none;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  cursor: var(--hoverCursorURL), pointer;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const MobileSideContent = styled.div`
  padding: 12px;

  @media (min-width: 769px) {
    display: none;
  }
`;

function GameUiHamburgerNav() {
  const homeSet = useRecoilValue(homeSettingState);
  const [isVisible, setIsVisible] = useRecoilState(gameUiVisibleState);
  const mainPhotocardData = useRecoilValue(mainPhotocardDataState);
  const isValidLogin = useValidLogin();
  const setOpenModal = useSetRecoilState(registerOpenState);
  const [showMobileNoticePanel, setShowMobileNoticePanel] = useState(false);
  const [showMobileQuestPanel, setShowMobileQuestPanel] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  if (!homeSet) return null;

  return (
    <>
      <Wrapper>
        {/* 캐릭터 패널 */}
        <CharacterPanel />
        {/* 재화/캐릭터버튼 등 */}
        <CharacterFuncSec />
        <FrontBtnWrap>
          <MusicButton color="var(--menuTextColor)" />
          <NotificationBtn
            dropdownDir="topRight"
            color="var(--menuTextColor)"
          />
          {masterSet.chat && <ChatBtn color="var(--menuTextColor)" />}

          {/* 토글 버튼 */}
          <GameUiToggleBtn />
        </FrontBtnWrap>

        {/* 퀘스트 + 공지 */}
        <MiniQuestWrapper isVisible={isVisible}>
          <CumuNotice type={POSTING_NOTICE_TYPE.ICONIMAGE} />
          <MiniQuest />
        </MiniQuestWrapper>

        {/* 모바일 플로팅 트리거 & 사이드 패널 - 공지 */}
        <MobileTriggerBtn
          onClick={() => setShowMobileNoticePanel((v) => !v)}
          aria-label="toggle-notice-side"
        >
          <Icon name="Notice20" color="var(--menuTextColor)" />
        </MobileTriggerBtn>
        <SideModalWrapper
          show={showMobileNoticePanel}
          direction="left"
          outsideClick={() => setShowMobileNoticePanel(false)}
        >
          <MobileSideContent>
            <CumuNotice type={POSTING_NOTICE_TYPE.ICONIMAGE} />
          </MobileSideContent>
        </SideModalWrapper>

        {/* 모바일 플로팅 트리거 & 사이드 패널 - 퀘스트 */}
        <MobileTriggerBtn
          onClick={() => setShowMobileQuestPanel((v) => !v)}
          aria-label="toggle-quest-side"
          style={{ top: 144 }}
        >
          <Icon name="Quest20" color="var(--menuTextColor)" />
        </MobileTriggerBtn>
        <SideModalWrapper
          show={showMobileQuestPanel}
          direction="left"
          outsideClick={() => setShowMobileQuestPanel(false)}
        >
          <MobileSideContent>
            <MiniQuest />
          </MobileSideContent>
        </SideModalWrapper>

        {/* 서브 메뉴 */}
        <SubMenuList isVisible={isVisible} />

        {/* 모바일: 재사용 사이드 메뉴 패널 */}
        <SideMenuPanel
          show={isVisible}
          onClose={() => setIsVisible(false)}
          header={
            <UserWrap onClick={(e) => e.stopPropagation()}>
              {isValidLogin ? (
                <UserSec
                  dropdownDir="topLeft"
                  showDisplayName
                  close={toggleVisibility}
                  soundEff={{}}
                />
              ) : (
                <Button shape="stroke" onClick={() => setOpenModal(true)}>
                  로그인
                </Button>
              )}
              <ActionButtons
                homeSet={homeSet}
                data={mainPhotocardData as IPhotoCard}
                isVisible={isVisible}
                direction={CHAR_PANEL_TYPE.TOPRIGHT}
              />
            </UserWrap>
          }
        />
      </Wrapper>
    </>
  );
}

export default GameUiHamburgerNav;
